import { CompensationMethod } from "@redotech/redo-return-core-common/compensation-method";
import * as z from "zod";

/** Team settings that gate a notification on top of its automation, plus the shipment reminder schedule. */
export const ReturnsShopperNotificationsSettingsChangesSchema = z.object({
  labelExpiration: z
    .object({
      reminderEmails: z.boolean(),
      expirationEmails: z.boolean(),
      reminderEmailDays: z.array(z.number().int().min(1)),
      compensationMethodsToRemind: z.array(z.enum(CompensationMethod)),
    })
    .partial()
    .optional(),
});

export type ReturnsShopperNotificationsSettingsChanges = z.infer<
  typeof ReturnsShopperNotificationsSettingsChangesSchema
>;

export interface ShipmentReminderSettings {
  /** Days after the label is created, each at most the label expiration. */
  reminderEmailDays: number[];
  /** Nothing sends while this is empty. */
  compensationMethodsToRemind: CompensationMethod[];
}

/** The slice of `team.settings` these notifications read. */
export interface ReturnsShopperNotificationsSettingsSource {
  labelExpiration?: {
    days?: number | null;
    reminderEmails?: boolean | null;
    expirationEmails?: boolean | null;
    reminderEmailDays?: number[] | null;
    compensationMethodsToRemind?: CompensationMethod[] | null;
  } | null;
}

export function shipmentReminderSettingsFromTeam(
  settings: ReturnsShopperNotificationsSettingsSource,
): ShipmentReminderSettings {
  return {
    reminderEmailDays: sortedReminderDays(
      settings.labelExpiration?.reminderEmailDays ?? [],
    ),
    compensationMethodsToRemind:
      settings.labelExpiration?.compensationMethodsToRemind ?? [],
  };
}

function sortedReminderDays(days: readonly number[]): number[] {
  return [...new Set(days)].sort((a, b) => a - b);
}

function sameMembers(a: readonly string[], b: readonly string[]): boolean {
  return a.length === b.length && a.every((item) => b.includes(item));
}

/** Writes only values that differ from what's stored. Reminder days past the label expiration are dropped. */
export function returnsShopperNotificationsTeamPatchForChanges(
  settings: ReturnsShopperNotificationsSettingsSource,
  changes: ReturnsShopperNotificationsSettingsChanges,
): { set: Record<string, unknown>; unset: string[] } {
  const stored = settings.labelExpiration;
  const requested = changes.labelExpiration;
  const set: Record<string, unknown> = {};
  if (
    requested?.reminderEmails !== undefined &&
    requested.reminderEmails !== !!stored?.reminderEmails
  ) {
    set["labelExpiration.reminderEmails"] = requested.reminderEmails;
  }
  if (
    requested?.expirationEmails !== undefined &&
    requested.expirationEmails !== !!stored?.expirationEmails
  ) {
    set["labelExpiration.expirationEmails"] = requested.expirationEmails;
  }
  const storedReminders = shipmentReminderSettingsFromTeam(settings);
  if (requested?.reminderEmailDays !== undefined) {
    const maxDay = stored?.days ?? Infinity;
    const days = sortedReminderDays(requested.reminderEmailDays).filter(
      (day) => day <= maxDay,
    );
    if (
      days.length !== storedReminders.reminderEmailDays.length ||
      days.some((day, i) => day !== storedReminders.reminderEmailDays[i])
    ) {
      set["labelExpiration.reminderEmailDays"] = days;
    }
  }
  if (
    requested?.compensationMethodsToRemind !== undefined &&
    !sameMembers(
      requested.compensationMethodsToRemind,
      storedReminders.compensationMethodsToRemind,
    )
  ) {
    set["labelExpiration.compensationMethodsToRemind"] =
      requested.compensationMethodsToRemind;
  }
  return { set, unset: [] };
}
